import { forwardRef, useState, type ReactNode } from 'react'
import { IconStateProvider } from '../icons/fromLucide'
import type { IconState } from '../icons/state'
import { cn } from '../lib/cn'
import { Tooltip } from './Tooltip'

type Size = 'sm' | 'md' | 'lg'
type Tone = 'quiet' | 'solid'

const SIZE: Record<Size, string> = {
  sm: 'size-[var(--control-sm)]',
  md: 'size-[var(--control-md)]',
  lg: 'size-[var(--control-lg)]',
}

/** The chip carries the state. The glyph never darkens. */
const TONE: Record<Tone, string> = {
  quiet: 'text-muted-foreground hover:bg-tint active:bg-selected',
  solid: 'bg-primary text-primary-foreground hover:bg-primary/85 [&_.wf-icon]:text-primary-foreground',
}

interface IconButtonProps {
  label: string
  'aria-expanded'?: boolean
  'aria-controls'?: string
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup']
  onClick?: () => void
  size?: Size
  tone?: Tone
  active?: boolean
  disabled?: boolean
  tooltipSide?: 'top' | 'bottom' | 'right'
  className?: string
  children: ReactNode
}

/**
 * The one icon-only control. Always a circle, always tooltip-labelled, and it
 * does not scale under the pointer. It publishes its pointer state to the
 * marks inside it, so the whole control is live.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, onClick, size = 'md', tone = 'quiet', active = false, disabled = false, tooltipSide = 'top', className, children, ...aria },
  ref,
) {
  const [pointer, setPointer] = useState<'rest' | 'hover' | 'press'>('rest')
  const state: IconState = disabled ? 'rest' : pointer === 'press' ? 'press' : active ? 'on' : pointer

  return (
    <Tooltip label={label} side={tooltipSide}>
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={aria['aria-expanded'] === undefined ? active || undefined : undefined}
        {...aria}
        onPointerEnter={() => setPointer('hover')}
        onPointerLeave={() => setPointer('rest')}
        onPointerDown={() => setPointer('press')}
        onPointerUp={() => setPointer('hover')}
        onPointerCancel={() => setPointer('rest')}
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center rounded-full leading-none',
          'transition-colors duration-fast disabled:pointer-events-none disabled:opacity-40',
          SIZE[size],
          TONE[tone],
          active && tone === 'quiet' && 'bg-selected',
          className,
        )}
      >
        <IconStateProvider state={state}>{children}</IconStateProvider>
      </button>
    </Tooltip>
  )
})
